import React, { useEffect, useState } from 'react';

interface SixDigitCardProps {
    type?: 'email' | 'phone'
    emailOrPhone?: string;
    title: string;
    resetCode?: boolean;
    setResetCode?: React.Dispatch<React.SetStateAction<boolean>>;
}

const SixDigitCard: React.FC<SixDigitCardProps> = ({ type = 'email', emailOrPhone, title, resetCode, setResetCode }) => {
    const [code, setCode] = useState(Array(6).fill(''));
    const [error, setError] = useState('');
    const [timer, setTimer] = useState(60);
    const [resendDisabled, setResendDisabled] = useState(true);

    useEffect(() => {
        if (timer > 0) {
            const countdown = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(countdown);
        } else {
            setResendDisabled(false);
        }
    }, [timer]);

    useEffect(() => {
        if (resetCode) {
            setCode(Array(6).fill(''));
            setResetCode && setResetCode(false); // Reset the resetCode state in the parent component
        }
    }, [resetCode, setResetCode]);

    const handleResend = () => {
        // Logic to resend the verification code
        console.log('Verification code resent.');
        setTimer(60);
        setResendDisabled(true);
        setCode(Array(6).fill(''));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value;
        if (value.match(/^[0-9]$/)) {
            const newCode = [...code];
            newCode[index] = value;
            setCode(newCode);
            setError('');
            // Move to the next input field
            if (index < 5 && value) {
                document.getElementById(`code-${index + 1}`)?.focus();
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if ((e.key === 'Backspace' || e.key === 'Delete')) {
            const newCode = [...code];
            if (newCode[index]) {
                newCode[index] = '';
                setCode(newCode);
            } else if (index > 0) {
                document.getElementById(`code-${index - 1}`)?.focus();
                newCode[index - 1] = '';
                setCode(newCode);
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const verificationCode = code.join('');
        try {
            if (verificationCode.length === 6) {
                console.log('Verification code submitted:', verificationCode);
                return;
            } else {
                setError('Invalid verification code. Please try again.');
                setCode(Array(6).fill(''));
            }
        } catch (error) {
            console.error('An error occurred. Please try again.');
        }
    };

    return (
        <form onSubmit={handleSubmit} className='container w-full sm:w-[400px] space-y-3 order-1 md:order-2'>
            <h1 className='text-3xl font-semibold text-primaryColor text-left'>{title}</h1>
            <p className='text-primaryColor text-left'>We sent your code to {type === 'email' ? emailOrPhone : `+${emailOrPhone}`}</p>
            <div className="flex justify-between mb-6">
                {code.map((digit, index) => (
                    <input
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        className={`w-12 h-12 text-center border rounded mx-1 focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : 'border-gray-300'}`}
                    />
                ))}
            </div>
            {error && <p className="text-red-500 text-center mb-6">{error}</p>}
            <div className="flex items-center gap-2 mb-6">
                <span className="text-primaryColor">Didn{"'"}t get code?</span>
                <button
                    type="button"
                    onClick={handleResend}
                    className={`text-primaryColor font-bold ${resendDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:underline'}`}
                    disabled={resendDisabled}
                >
                    Resend in {timer > 0 ? `${timer}s` : '60s'}
                </button>
            </div>
            <button
                type="submit"
                className="bg-primaryColor text-white py-2 rounded-md w-full hover:bg-primaryColorLighter disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={code.join('').length < 6}
            >
                Continue
            </button>
        </form>
    );
};

export default SixDigitCard;
